import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { UserContext } from '../../UserContext'; // ✅ Import User Context

const LifelongLearning = () => {
  const { user } = useContext(UserContext); // ✅ Get user from context
  const category = 'lifelonglearning'; // ✅ Category for fetching videos
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await axios.get(`https://wholeness-forward.onrender.com/api/video/videos/${category}`);
        if (Array.isArray(response.data)) {
          setVideos(response.data);
        }
      } catch (error) {
        console.error('Error fetching videos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  // ✅ Handle Mark as Helpful for logged-in users only
  const handleMarkHelpful = async (videoId) => {
    if (!user || !user.id) {
      alert('You must be logged in to mark as helpful.');
      return;
    }

    try {
      const response = await axios.post(`https://wholeness-forward.onrender.com/api/video/videos/toggleHelpful`, {
        videoId,
        userId: user.id,
      });

      // ✅ Update UI with new helpful count
      setVideos((prevVideos) =>
        prevVideos.map((video) =>
          video._id === videoId
            ? { ...video, helpfulCount: response.data.helpfulCount }
            : video
        )
      );
    } catch (error) {
      console.error('Error marking video as helpful:', error);
    }
  };

  return (
    <div className="page-container lifelong-learning-page">
      <h1 className="page-title">Lifelong Learning</h1>

      {/* Include the image */}
      <img
        src="/images/wf-lifelonglearning.webp"
        alt="Lifelong Learning"
        className="page-image"
      />

      <p className="page-intro">
        Welcome to the Lifelong Learning section of Wholeness Forward. Lifelong learning is the continuous pursuit of knowledge and skills throughout an individual's life.
      </p>

      <p className="page-text">
        In today's fast-paced world, the ability to learn, unlearn, and relearn is more important than ever. Lifelong learning not only enhances intellectual growth but also strengthens social connections, boosts confidence, and provides a sense of purpose.
      </p>

      <hr className="page-divider" />

      <h2 className="section-title">Health Benefits of Lifelong Learning</h2>
      <ul className="content-list">
        <li><strong>Improved Brain Health:</strong> Regularly challenging your brain with new knowledge helps maintain cognitive function and memory.</li>
        <li><strong>Mental Stimulation:</strong> Keeps your mind active and engaged, reducing mental stagnation.</li>
        <li><strong>Stress Reduction:</strong> Exploring new interests can be therapeutic and reduce stress.</li>
        <li><strong>Emotional Resilience:</strong> Enhances self-esteem and confidence, which are essential for emotional well-being.</li>
        <li><strong>Social Connections:</strong> Learning in group settings fosters interaction and community.</li>
        <li><strong>Increased Longevity:</strong> Lifelong learning is linked to better overall health and may contribute to longer life expectancy.</li>
      </ul>

      <hr className="page-divider" />

      <h2 className="section-title">Curated Video Collection</h2>
      <p className="section-description">Watch videos to discover the benefits of lifelong learning and practical strategies for integrating it into your life:</p>

      {loading ? (
        <p className="loading-message">Loading videos...</p>
      ) : (
        <div className="video-container">
          {videos.length > 0 ? (
            videos.map((video) => (
              <div key={video._id} className="video-card">
                <h3 className="video-title">{video.title}</h3>
                <p className="video-details">
                  <strong>Source:</strong> {video.source} <br />
                  <strong>Length:</strong> {video.length} <br />
                  {video.description}
                </p>
                <iframe
                  className="video-frame"
                  src={video.url}
                  title={video.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
                <div className="video-actions">
                  {/* ✅ Helpful Button */}
                  <button
                    className="button"
                    onClick={() => handleMarkHelpful(video._id)}
                    disabled={video.usersWhoClickedHelpful?.includes(user?.id)}
                  >
                    {video.usersWhoClickedHelpful?.includes(user?.id)
                      ? `Helpful (${video.helpfulCount})`
                      : `Mark as Helpful (${video.helpfulCount || 0})`}
                  </button>
                </div>
                <hr />
              </div>
            ))
          ) : (
            <p className="no-videos-message">No videos found for this category.</p>
          )}
        </div>
      )}

      <hr className="page-divider" />

      <h3 className="subheading">Community Lifelong Learning Discussion</h3>
      <p className="page-text">
        Share your learning experiences, ask questions, and gain insights from others on the lifelong learning journey. Join the conversation in our community forum.
      </p>
      <a href="/forum/Lifelong_Learning" className="btn">Join the Forum</a>
    </div>
  );
};

export default LifelongLearning;
